import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { Link } from "react-router-dom";
import { Hash, Megaphone, ArrowRight } from "lucide-react";
import { formatDateTime } from "@/lib/dateFormat";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return formatDateTime(d);
}

/**
 * Feb 2026 — Information Cleanse: this used to be a per-project private
 * notes board. Project-specific discussion now lives in the project's
 * Chat Sidecar instead, so this slot is repurposed into a single,
 * read-only, studio-wide "Internal Feed" — a merged view of #general +
 * #announcements, giving the whole studio one shared source for culture &
 * news right on every project page. Posting happens from the Chat tab.
 */
export default function InternalUpdatesFeed() {
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-feed");
      setRows(data || []);
    } catch (e) { logger.debug("internal feed fetch failed", e?.message || e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-flat p-5 mb-6" data-testid="internal-updates-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-sm bg-slate-900 text-white flex items-center justify-center"><Hash size={13} /></span>
          <div>
            <div className="label-overline">Internal feed</div>
            <p className="text-[11px] text-slate-500 mt-0.5">#general + #announcements — studio culture, in one place.</p>
          </div>
        </div>
        <Link to="/chat" className="text-[11px] font-medium text-[#F54800] hover:underline inline-flex items-center gap-0.5 shrink-0" data-testid="internal-feed-open-chat">
          Open Chat <ArrowRight size={11} />
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-6" data-testid="internal-updates-empty">
          No studio chatter yet — say hi in #general.
        </div>
      ) : (
        <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="internal-updates-list">
          {rows.map((m) => (
            <li key={m.id} className="border-l-2 border-slate-900 pl-3 py-1" data-testid={`internal-feed-item-${m.id}`}>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                {m.channel_name === "announcements"
                  ? <Megaphone size={10} className="text-[#F54800]" />
                  : <Hash size={10} className="text-slate-400" />}
                <span className="font-semibold text-slate-700">{m.sender_name || "Studio"}</span>
                <span className="text-slate-400">· #{m.channel_name} · {fmtWhen(m.created_at)}</span>
              </div>
              <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{m.text}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
